import { computePosition, autoUpdate, inline, shift, flip, offset, arrow, Placement } from '@floating-ui/dom';

export class Popup {
	id: string;
	root: HTMLElement;
	popup: HTMLElement;
	template: HTMLTemplateElement;
	popupContent: HTMLElement;
	arrow: HTMLElement;

	dismountObserver?: MutationObserver;

	container?: HTMLElement;
	placement?: Placement;
	width?: string;

	autoUpdateCleanup?: () => void;

	constructor(root: HTMLElement) {
		const id = root.getAttribute('data-popup');
		if (!id) throw new Error('[ui:popup] No popup ID specified.');

		this.id = id;
		this.root = root;
		this.root.classList.add('popup-root');

		const template = document.getElementById(id);
		if (!template || template.tagName !== 'TEMPLATE') throw new Error(`[ui:popup] Unable to find corresponding <template> for popup "${id}".`);
		this.template = template as HTMLTemplateElement;

		const popup = document.createElement('div');
		popup.className = 'popup';
		popup.id = `${id}-popup`;
		this.popup = popup;

		const popupContent = document.createElement('div');
		popupContent.className = 'popup-content';
		this.popupContent = popupContent;
		this.popup.appendChild(popupContent);

		const arrow = document.createElement('span');
		arrow.className = 'popup-arrow';
		this.arrow = arrow;
		this.popup.appendChild(arrow);

		// parse popup options
		this.placement = this.root.getAttribute('data-popup-placement') as Placement || undefined;
		this.width = this.root.getAttribute('data-popup-width') || undefined;
		if (this.width) this.popup.style.maxWidth = this.width;
		const containerSelector = this.root.getAttribute('data-tooltip-container');
		const container = containerSelector ? document.querySelector(containerSelector) : null;
		this.container = container as HTMLElement || undefined;

		/**
		 * Clone the template & render into the popup content root.
		 *
		 * Note: we only do this once since the template content might get mutated by the parent code.
		 * It's still up in the air whether this is a good idea or not; and the overall premise of
		 * popups that can be integrated in a native JS component library that can play nicely with SPA
		 * frameworks like React or Svelte is still largely untested for our use cases.
		 */
		const clone = this.template.content.cloneNode(true);
		while (this.popupContent.lastChild) this.popupContent.removeChild(this.popupContent.lastChild);
		this.popupContent.appendChild(clone);

		// register popup events
		this.root.addEventListener('click', this.show);
		document.addEventListener('click', this.onDocumentClick);
	}

	update = async () => {
		const { x, y, middlewareData, placement } = await computePosition(this.root, this.popup, {
			placement: this.placement || 'top',
			middleware: [
				inline(),
				shift({ padding: 16 }),
				offset(12),
				flip(),
				arrow({ element: this.arrow, padding: 5 })
			]
		});

		this.popup.style.left = `${x}px`;
		this.popup.style.top = `${y}px`;

		if (middlewareData.arrow) {
			const side = placement.split('-')[0];
			const staticSide = {
				top: 'bottom',
				right: 'left',
				bottom: 'top',
				left: 'right'
			}[side] || 'top';

			const { x, y } = middlewareData.arrow;
			this.arrow.style.left = x != null ? `${x}px` : '';
			this.arrow.style.top = y != null ? `${y}px` : '';
			this.arrow.style.setProperty(staticSide, `${-this.arrow.offsetWidth / 2}px`);
		}
	};

	show = () => {
		// If there is a container provided, render the popup there.
		// Otherwise, put it at the document root.
		if (this.container) this.container.appendChild(this.popup);
		else document.body.appendChild(this.popup);

		// Start a document-wide mutation observer to check if the root element is dismounted from
		// the document.
		if (this.dismountObserver) this.dismountObserver.disconnect();
		this.dismountObserver = new MutationObserver(() => {
			if (!this.root.isConnected) {
				this.hide();
				this.dismountObserver?.disconnect();
				delete this.dismountObserver;
			}
		});
		this.dismountObserver.observe(document, { childList: true, subtree: true });

		// Render the popup & auto-update on position changes.
		this.autoUpdateCleanup = autoUpdate(this.root, this.popup, this.update);
	};

	hide = () => {
		if (this.dismountObserver) this.dismountObserver.disconnect();
		if (this.popup.isConnected) this.popup.parentElement?.removeChild(this.popup);
		if (this.autoUpdateCleanup) this.autoUpdateCleanup();

		this.root.focus();
	};

	onDocumentClick = (evt: MouseEvent) => {
		if (!document.body.contains(this.popup)) return;

		// check if user clicked a "dismiss" button specifically
		const dismissButtons = document.querySelectorAll(`[data-popup-dismiss=${this.id}]`);
		for (const button of Array.from(dismissButtons)) {
			if (evt.composedPath().includes(button)) {
				evt.preventDefault();
				this.hide();
				return;
			}
		}

		// let clicks inside the popup or the root trigger to pass through
		const isPopupClicked = evt.composedPath().includes(this.popup);
		const isRootClicked = evt.composedPath().includes(this.root);
		if (isPopupClicked || isRootClicked) return;

		// user clicked outside the popup, block the click & close the popup
		evt.preventDefault();
		this.hide();
	};
}

export const init = () => {
	const popups = Array.from(document.querySelectorAll<HTMLElement>('[data-popup]'));
	if (!window.StandardUI.popup) window.StandardUI.popup = { init, instances: [] };

	for (const root of popups) {
		if (!window.StandardUI.popup.instances.find(x => x.root === root)) {
			const popup = new Popup(root);
			window.StandardUI.popup.instances.push(popup);
		}
	}
};

export default {
	init
};
